// The agenda's time-range filter, as a single definition.
//
// The slider reports percentages across the day's dynamic window
// (start_hour..start_hour+window_hours, the real programming bounds rather than
// a fixed 9-20). sessions.get.js applies this to the catalog server-side; the
// user's own custom events are merged in on the client and never reach that
// filter, so agenda.vue applies the same predicate to them.
//
// Both ends round to the nearest quarter hour, so the filter agrees with the
// 15-minute-rounded time label the slider shows — e.g. both 81% and 82% read
// "6:00pm", and an item ending at 6:00pm is included at either.

// Decimal hour for a percentage across [startHour, endHour].
export function timeRangeHour(percent, startHour, endHour) {
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    return Math.round((startHour + ((pct / 100) * (endHour - startHour))) * 4) / 4;
}

// Decimal hour-of-day for an instant, in the conference timezone.
export function hourOfDay(value, tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric', minute: 'numeric', hour12: false, timeZone: tz,
    }).formatToParts(new Date(value));
    const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
    const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
    return h + m / 60;
}

/**
 * Does the item fall within the selected range?
 *
 * OVERLAP, not start-time. "Between 11:30 and 6" means everything happening in
 * that window, including something that began at 11:15 and is still running —
 * asking for your 11:30 onwards and being shown nothing until 12:30, because
 * the thing you are in started fifteen minutes early, is simply wrong. It also
 * left visible holes on the calendar, whose grid renders from the top of the
 * hour and so drew empty space over a real commitment.
 *
 * It decomposes onto the two handles: the start handle keeps anything still
 * running at that time, the end handle keeps anything already started.
 *
 * `end` at 100 means "no upper limit" and skips the upper bound entirely, so
 * evening programming past the slider's nominal top is not dropped. All-day
 * items are always in range.
 */
export function overlapsTimeRange(item, { start, end, startHour, endHour, tz }) {
    if (item?.is_all_day) return true;

    if (start) {
        const hour = timeRangeHour(start, startHour, endHour);
        // Still running when the window opens. Falls back to the start instant
        // for an item with no end, which is then treated as a moment in time.
        const endsAt = item?.end_date_and_time ?? item?.start_date_and_time;
        if (!endsAt) return false;
        if (hourOfDay(endsAt, tz) <= hour) return false;
    }

    if (end && Number(end) < 100) {
        const hour = timeRangeHour(end, startHour, endHour);
        // Already started before the window closes.
        if (!item?.start_date_and_time) return false;
        if (hourOfDay(item.start_date_and_time, tz) >= hour) return false;
    }

    return true;
}
